import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Spinner } from 'reactstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { AssetsVehiclesFormData } from '../../../Public/FormData';
import { useLocation } from 'react-router-dom';
import { RootState } from '../../../../../ReduxToolkit/Store';
import { useGetRequestQuery } from '../../../../../Hooks/useGetRequestQuery';
import { getClientVehicleAssetsByClientIdService, getClientVehicleAssetsByIdService, insertOrUpdateClientVehicleAssetsService, softDeleteVehicleAssetsByIdService } from '../../../../../Services/PublicPageServices';
import { useMutationRequestQuery } from '../../../../../Hooks/useMutationRequestQuery';
import { H3, H6, LI, UL } from '../../../../../AbstractElements';
import TextField from '../../../../common/TextField';
import DollarInput from '../../../../common/DollarInput';
import Dropdown from '../../../../common/DropDown';
import DecimalInput from '../../../../common/DecimalInput';
const customStyles = {
    head: {
        style: {
            fontWeight: '800',
            color: '#0000ff',
            fontSize: '14px'
        },
    },
};
export default function FactFindMotorVehicles({ clientFactsFindData, clientId }: any) {
    const [formData, setFormData] = useState<any>(AssetsVehiclesFormData);
    const location = useLocation();
    const isMMPAssetsLiabilitiesRoute = location.pathname === '/MMPAssetsLiabilities';
    const userData = useSelector((state: RootState) => state.auth);
    const [assetsPropertyData, setAssetsPropertyData] = useState([]);
    const [selectedRowId, setSelectedRowId] = useState<number | null>(null);


    const {
        data: assetsProperty,
        refetch: refetchAssetsProperty,
        isLoading: assetsPropertyLoading,
        isError: assetsPropertyIsError,
    } = useGetRequestQuery(
        ['getClientVehicleAssetsByClientIdService', clientId],
        () => getClientVehicleAssetsByClientIdService(clientId as any),
        {
            enabled: true,
        }
    );

    const {
        data: assetsPropertyFormData,
        isLoading: assetsPropertyFormDataLoading,
        isError: assetsPropertyFormDataIsError,
    } = useGetRequestQuery(
        ["getClientVehicleAssetsByIdService", selectedRowId],
        () => getClientVehicleAssetsByIdService(selectedRowId),
        {
            enabled: !!selectedRowId,
        }
    );

    useEffect(() => {
        if (assetsProperty?.data && !assetsPropertyIsError && !assetsPropertyLoading) {
            setAssetsPropertyData(assetsProperty?.data);
        }
    }, [assetsProperty, assetsPropertyIsError, assetsPropertyLoading]);

    useEffect(() => {
        if (assetsPropertyFormData?.data && !assetsPropertyFormDataIsError && !assetsPropertyFormDataLoading) {
            if (assetsPropertyFormData?.data?.id) {
                setSelectedRowId(assetsPropertyFormData?.data?.id)
            }
            setFormData({
                vehiclesEstimatedValue: assetsPropertyFormData?.data?.estimatedValue,
                vehiclesMake: assetsPropertyFormData?.data?.make,
                vehiclesYear: assetsPropertyFormData?.data?.year,
                vehiclesCurrentLoan: assetsPropertyFormData?.data?.currentLoan,
                vehiclesLendersName: assetsPropertyFormData?.data?.lendersName,
                vehiclesInterestRate: assetsPropertyFormData?.data?.interestRate,
                vehiclesRepayment: assetsPropertyFormData?.data?.repayment,
                vehiclesRepaymentDLL: assetsPropertyFormData?.data?.frequency,
            })
        }
    }, [assetsPropertyFormData, assetsPropertyFormDataIsError, assetsPropertyFormDataLoading]);

    const { mutate: updateClientAssets, isPending: isUpdateClientAssetsPending } = useMutationRequestQuery(
        'insertOrUpdateClientVehicleAssetsService',
        insertOrUpdateClientVehicleAssetsService,
        {
            onSuccess: (data: any) => {
                toast.success("Assets vehicles information saved or updated successfully.");
                setFormData(AssetsVehiclesFormData)
                setSelectedRowId(null)
                refetchAssetsProperty()
            },
            onError: (error: any) => {
                toast.error("Failed to submit assets vehicles information");
            },
        }
    );


    const {
        mutate: deleteClientAssets,
        isPending: isDeleteClientAssetsPending,
    } = useMutationRequestQuery(
        'softDeleteVehicleAssetsByIdService',
        softDeleteVehicleAssetsByIdService,
        {
            onSuccess: (data: any) => {
                toast.success("Vehicle deleted successfully.");
                refetchAssetsProperty()
                setSelectedRowId(null)
            },
            onError: (error: any) => {
                toast.error("Failed to delete Property.");
                console.error(error);
            },
        }
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const requiredFields = [
            'vehiclesEstimatedValue',
            'vehiclesMake',
            'vehiclesYear',
            'vehiclesCurrentLoan',
            'vehiclesLendersName',
            'vehiclesRepayment',
            'vehiclesRepaymentDLL',
            'vehiclesInterestRate',
        ];
        for (let field of requiredFields) {
            if (!formData[field] || formData[field].trim() === "") {
                toast.error("Please fill all Motor Vehicles fields.");
                return;
            }
        }
        updateClientAssets({
            id: selectedRowId ? selectedRowId : "0",
            clientId: clientId,
            estimatedValue: formData.vehiclesEstimatedValue,
            make: formData.vehiclesMake,
            year: formData.vehiclesYear,
            currentLoan: formData.vehiclesCurrentLoan,
            lendersName: formData.vehiclesLendersName,
            repayment: formData.vehiclesRepayment,
            frequency: formData.vehiclesRepaymentDLL,
            interestRate: formData.vehiclesInterestRate,
        });
    };


    const handleEditClick = (id: number) => {
        setSelectedRowId(id);
    };

    const handleDeleteClick = (id: number) => {
        deleteClientAssets(id)
    };

    const handleValueChange = (name: string, value: string) => {
        setFormData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const splitDropdownValues = (dropdownValues: string) => {
        return dropdownValues ? dropdownValues.split(',').map(value => value.trim()) : [];
    };

    const zeroConfigurationTableColumns = [
        {
            name: "Action",
            cell: (row: any) => (
                <UL className="action simple-list flex-row">
                    <LI style={{ 'cursor': 'pointer' }} className="edit"><a onClick={() => { handleEditClick(row.id) }}><i className="icon-pencil-alt" /></a></LI>
                    <LI style={{ 'cursor': 'pointer' }} className="delete"><a onClick={() => { handleDeleteClick(row.id) }}><i className="icon-trash" /></a></LI>
                </UL>
            )
        },
        {
            name: "Estimated Value",
            selector: (row: any) => row["estimatedValue"],
            sortable: false,

        },
        {
            name: "Make",
            selector: (row: any) => row["make"],
            sortable: false,
        },
        {
            name: "Year",
            selector: (row: any) => row["year"],
            sortable: false,
        },
        {
            name: "Current Loan",
            selector: (row: any) => row["currentLoan"],
            sortable: false,
        },
        {
            name: "Lender's Name",
            selector: (row: any) => row["lendersName"],
            sortable: false,
        },
        {
            name: "Repayment",
            selector: (row: any) => row["repayment"],
            sortable: false,
        },
        {
            name: "Frequency",
            selector: (row: any) => row["frequency"],
            sortable: false,
        },
        {
            name: "Interest Rate",
            selector: (row: any) => row["interestRate"],
            sortable: false,
        },
    ];

    const handleCancel = () => {
        setSelectedRowId(null)
        setFormData(AssetsVehiclesFormData)
    };

    return (
        <div className={isMMPAssetsLiabilitiesRoute ? 'mmp-assets-liabilities container-fluid' : 'container-fluid'}>
            <div className="table-responsive h-100 py-4">
                <section className='bg-white'>

                    <div className="scrollable-table-container">
                        <table className="scrollable-table" style={{ marginLeft: '1%', marginTop: '3%' }}>
                            <div className="d-flex align-items-center">
                                <Col xs={12} className='d-flex'>
                                    <H3 className="heading-color f-w-900 text-left my-2">Motor Vehicles</H3>
                                    {/* <Button style={{ marginLeft: '10px' }} color="primary" disabled={isAssetsAndLiabilitiesLoading} onClick={handleHover}>
                                        {isAssetsAndLiabilitiesLoading ? "Loading . . ." : "Save"}
                                    </Button> */}
                                </Col>
                            </div>
                            {assetsPropertyLoading ? (
                                <div
                                    style={{
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        padding: '15px',
                                        backgroundColor: '#fff',
                                        textAlign: 'center',
                                    }}
                                    className='mt-4'
                                >
                                    <Spinner color="primary" />
                                </div>
                            ) : assetsPropertyData.length !== 0 ? (
                                <div
                                    style={{
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        padding: '5px',
                                        backgroundColor: '#fff',
                                    }}
                                    className='mt-4'
                                >
                                    <DataTable
                                        title=""
                                        columns={zeroConfigurationTableColumns}
                                        data={assetsPropertyData}
                                        responsive
                                        customStyles={customStyles}
                                    />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        padding: '15px',
                                        backgroundColor: '#fff',
                                        textAlign: 'center',
                                    }}
                                    className='mt-4'
                                >
                                    <h6>No vehicles have been added yet.</h6>
                                </div>
                            )}

                            {/* <div style={{ borderBottom: '1px solid blue', marginTop: '15px' , width : '27%'}}></div> */}
                            <Row className='mt-4' >
                                <div className='d-flex'>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '10%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Make</H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '7%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Year</H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Estimated Value</H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Current Loan</H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '10%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Lenders Name</H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Repayment</H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Frequency</H6>
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <H6 className="heading-color f-w-900 text-left">Interest Rate</H6>
                                    </Col>
                                </div>
                                <div className='d-flex item-center mt-2'>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '10%', paddingInline: '2px' }}>
                                        <TextField
                                            label=""
                                            type="text"
                                            name="vehiclesMake"
                                            value={formData.vehiclesMake}
                                            setValue={handleValueChange}
                                            design="default"
                                            tabIndex={67}
                                            maxChars={15}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '7%', paddingInline: '2px' }}>
                                        <TextField
                                            label=""
                                            type="number"
                                            name="vehiclesYear"
                                            value={formData.vehiclesYear}
                                            setValue={handleValueChange}
                                            placeholder="YYYY"
                                            design="default"
                                            maxChars={4}
                                            tabIndex={68}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <DollarInput
                                            label=""
                                            name="vehiclesEstimatedValue"
                                            value={formData.vehiclesEstimatedValue}
                                            setValue={handleValueChange}
                                            limit={6}
                                            design="default"
                                            tabIndex={69}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <DollarInput
                                            label=""
                                            name="vehiclesCurrentLoan"
                                            value={formData.vehiclesCurrentLoan}
                                            setValue={handleValueChange}
                                            limit={6}
                                            design="default"
                                            tabIndex={70}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '10%', paddingInline: '2px' }}>
                                        <TextField
                                            label=""
                                            type="text"
                                            name="vehiclesLendersName"
                                            value={formData.vehiclesLendersName}
                                            setValue={handleValueChange}
                                            maxChars={15}
                                            tabIndex={71}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <DollarInput
                                            label=""
                                            name="vehiclesRepayment"
                                            value={formData.vehiclesRepayment}
                                            setValue={handleValueChange}
                                            limit={6}
                                            tabIndex={72}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <Dropdown
                                            label=""
                                            name="vehiclesRepaymentDLL"
                                            value={formData.vehiclesRepaymentDLL}
                                            setValue={handleValueChange}
                                            options={splitDropdownValues(clientFactsFindData ? clientFactsFindData[11].dropDownValues : '')}
                                            tabIndex={73}
                                        />
                                    </Col>
                                    <Col xs={12} className='responsive-col-motorvehical' style={{ width: '9%', paddingInline: '2px' }}>
                                        <DecimalInput
                                            label=""
                                            name="vehiclesInterestRate"
                                            value={formData.vehiclesInterestRate}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            limit={4}
                                            tabIndex={74}
                                        />
                                    </Col>
                                </div>
                            </Row>
                        </table>
                        <Button
                            disabled={isUpdateClientAssetsPending}
                            type="button"
                            color="primary"
                            onClick={handleSubmit}
                            style={{ fontSize: '16px', marginBottom: '7px' }}
                        >
                            {isUpdateClientAssetsPending ? 'Loading . . .' : selectedRowId ? "Update Vehicle" : "Add New Vehicle"}
                        </Button>
                        {!selectedRowId && <Button
                            type="button"
                            color="secondary"
                            onClick={handleCancel}
                            style={{ marginBottom: '7px', fontSize: "16px", marginLeft: '20px', width: '180px' }}
                        >
                            Cancel
                        </Button>}
                    </div>
                </section>
            </div>
        </div>
    );
}

